import { CHIP_CLASS_NAME, CHIP_INLINE_COLORS } from "../constants";
import type { ChipOption } from "../types";

/**
 * Converts editor HTML (containing chip `<span>` elements) to storage format
 * where chips become plain `{{id}}` placeholders.
 *
 * Used on SAVE: before sending to API.
 *
 * Example:
 *   Input:  `<p>Hi <span data-type="variable-chip" data-id="customerName">...</span></p>`
 *   Output: `<p>Hi {{customerName}}</p>`
 */
export const serializeChipsToPlaceholders = (html: string): string => {
  if (!html) return "";

  try {
    const doc = new DOMParser().parseFromString(html, "text/html");

    const chipSelector = [
      'span[data-type="variable-chip"]',
      `span.${CHIP_CLASS_NAME}`,
    ].join(", ");

    doc.querySelectorAll(chipSelector).forEach((el) => {
      const id = el.getAttribute("data-id") || "";
      if (id) {
        el.replaceWith(doc.createTextNode(`{{${id}}}`));
      }
    });

    return doc.body.innerHTML;
  } catch {
    return html;
  }
};

/**
 * Converts storage format (containing `{{id}}` placeholders) back to editor
 * HTML with full chip `<span>` elements for TipTap to parse into nodes.
 *
 * Used on LOAD: when populating the editor for editing.
 *
 * The generated HTML must match the structure produced by
 * `variableChipExtension.ts` `renderHTML()` so TipTap's `parseHTML()` can
 * reconstitute chip nodes correctly.
 *
 * Unknown variable IDs (not found in chipOptions) are left as `{{id}}`.
 */
export const deserializePlaceholdersToChips = (
  html: string,
  chipOptions: ChipOption[],
): string => {
  if (!html) return "";

  const optionMap = new Map<string, ChipOption>();
  for (const opt of chipOptions) {
    optionMap.set(opt.id, opt);
  }

  return html.replace(/\{\{(\w[\w-]*)\}\}/g, (_match, id: string) => {
    const option = optionMap.get(id);
    if (!option) return _match;

    const bgColor = option.bgColor || CHIP_INLINE_COLORS.backgroundColor;
    const fontColor = option.fontColor || CHIP_INLINE_COLORS.textColor;
    const borderColor = CHIP_INLINE_COLORS.borderColor;

    return (
      `<span data-type="variable-chip" class="${CHIP_CLASS_NAME}"` +
      ` data-id="${id}" data-label="${option.label}" data-value="${option.value}"` +
      ` contenteditable="false" style="display:inline-block; margin:0 2px;">` +
      `<span style="display:inline-flex;align-items:center;height:24px;gap:8px;` +
      `border:1px solid ${borderColor};border-radius:4px;padding:2px 6px;` +
      `background-color:${bgColor};cursor:default;white-space:nowrap;">` +
      `<span class="chip-text" style="font-size:12px; vertical-align:middle;` +
      ` color:${fontColor}; display:inline-flex; align-items:center;">` +
      `${option.label}</span></span></span>`
    );
  });
};
